import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicPageShell from '../components/PublicPageShell';
import { Input } from '../components/ui/input';
import { cmsApi } from '../lib/api';
import {
  BookOpen, MagnifyingGlass, VideoCamera, FileText, Newspaper, ChatsCircle,
  Question, Download, Sparkle, ArrowRight, Code, Lightbulb, Calendar,
} from '@phosphor-icons/react';

const RESOURCE_CATEGORIES = [
  {
    key: 'docs',
    label: 'Documentation',
    desc: 'API references, SDKs, and integration guides for engineers.',
    icon: BookOpen,
    tint: '#002677',
    cta: 'Browse docs',
  },
  {
    key: 'tutorials',
    label: 'Tutorials',
    desc: 'Step-by-step walkthroughs for admins and end users.',
    icon: Lightbulb,
    tint: '#FFD100',
    cta: 'Start learning',
  },
  {
    key: 'webinars',
    label: 'Webinars',
    desc: 'Live and on-demand sessions with product experts.',
    icon: VideoCamera,
    tint: '#E4002B',
    cta: 'Watch sessions',
  },
  {
    key: 'whitepapers',
    label: 'Whitepapers',
    desc: 'Deep dives, research reports and industry benchmarks.',
    icon: FileText,
    tint: '#8347AD',
    cta: 'Download PDFs',
  },
  {
    key: 'blog',
    label: 'Blog',
    desc: 'Product updates, customer stories, and engineering deep dives.',
    icon: Newspaper,
    tint: '#41B6E6',
    cta: 'Read the blog',
  },
  {
    key: 'community',
    label: 'Community forum',
    desc: 'Ask questions, share templates, and meet other BIL users.',
    icon: ChatsCircle,
    tint: '#00B140',
    cta: 'Join the community',
  },
];

const DEFAULT_FEATURED = [
  {
    kind: 'Whitepaper',
    kind_tint: '#8347AD',
    title: 'The 2026 Deal Readiness Benchmark',
    desc: '240 M&A transactions analysed — the 7 levers that correlate with a 30%+ faster close.',
    cta: 'Download PDF',
  },
  {
    kind: 'On-demand webinar',
    kind_tint: '#E4002B',
    title: 'AI Copilots for Private Equity',
    desc: '45-min session with Helix Partners + BIL product team on GP-grade automation.',
    cta: 'Watch replay',
  },
  {
    kind: 'Customer story',
    kind_tint: '#41B6E6',
    title: 'How Meridian cut tool spend 62%',
    desc: 'Retail group consolidated 14 tools into BIL — and gained real-time P&L by store.',
    cta: 'Read story',
  },
];

const DEFAULT_UPCOMING_EVENTS = [
  { date: 'Mar 12', title: 'BIL Spring Launch 2026',          location: 'Virtual · Keynote + 12 sessions', cta: 'Register' },
  { date: 'Apr 04', title: 'AI for M&A Masterclass',           location: 'London · Hosted at The Shard',    cta: 'RSVP' },
  { date: 'May 22', title: 'Procurement Summit — Munich',      location: 'In-person · 2-day programme',     cta: 'Reserve seat' },
];

const POPULAR_DOCS = [
  { label: 'Quickstart — 10-minute setup',           tag: 'docs' },
  { label: 'REST API reference',                     tag: 'api' },
  { label: 'Webhooks & change streams',              tag: 'api' },
  { label: 'RBAC + SSO best practices',              tag: 'security' },
  { label: 'Data rooms 101',                         tag: 'product' },
  { label: 'Importing from HubSpot / Salesforce',    tag: 'migration' },
];

export default function ResourcesPage() {
  const [q, setQ] = useState('');
  const [featured, setFeatured] = useState(DEFAULT_FEATURED);
  const [events, setEvents] = useState(DEFAULT_UPCOMING_EVENTS);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [resRes, evRes] = await Promise.all([
          cmsApi.listPublic('resource'),
          cmsApi.listPublic('event'),
        ]);
        if (cancelled) return;
        if (Array.isArray(resRes.data) && resRes.data.length > 0) {
          setFeatured(resRes.data.map((i) => i.data || {}));
        }
        if (Array.isArray(evRes.data) && evRes.data.length > 0) {
          setEvents(evRes.data.map((i) => i.data || {}));
        }
      } catch { /* keep defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const filteredDocs = POPULAR_DOCS.filter((d) => !q || d.label.toLowerCase().includes(q.toLowerCase()));
  return (
    <PublicPageShell
      testId="resources-page"
      eyebrow="Resources"
      title="Learn, build, and grow with BIL."
      subtitle="Everything you need — docs, tutorials, whitepapers, webinars and a global community of practitioners."
      ctaPrimary={{ label: 'See pricing', to: '/pricing' }}
      ctaSecondary={{ label: 'Start free', to: '/register' }}
    >
      {/* Search */}
      <section className="py-12 bg-background">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="relative">
            <MagnifyingGlass size={20} className="absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search docs, tutorials, webinars…"
              className="pl-14 h-14 rounded-full text-base shadow-sm"
              data-testid="resources-search"
            />
          </div>
        </div>
      </section>

      {/* Category grid */}
      <section className="py-8 bg-background">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="resource-categories">
            {RESOURCE_CATEGORIES.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.key}
                  className="bg-card border border-border rounded-sm p-6 hover:border-foreground/30 hover:shadow-lg transition-all flex flex-col"
                  data-testid={`resource-${c.key}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="h-11 w-11 rounded-sm flex items-center justify-center"
                      style={{ background: `${c.tint}1A` }}
                    >
                      <Icon size={22} weight="duotone" color={c.tint} />
                    </div>
                    <h3 className="font-heading text-lg font-semibold tracking-tight">
                      {c.label}
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                    {c.desc}
                  </p>
                  <Link
                    to={`/resources/${c.key}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold hover:gap-2.5 transition-all"
                    style={{ color: c.tint }}
                    data-testid={`resource-cta-${c.key}`}
                  >
                    {c.cta} <ArrowRight size={14} weight="bold" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured content */}
      <section className="py-16 lg:py-20 bg-[#f5f6fa] dark:bg-muted/20 border-y border-border">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#002677] mb-2">
                Featured
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
                Hand-picked for executives & operators.
              </h2>
            </div>
            <Link
              to="/resources/blog"
              className="text-sm font-semibold text-[#002677] hover:underline inline-flex items-center gap-1.5"
            >
              View all <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="resource-featured">
            {featured.map((f, i) => {
              const tint = f.kind_tint || f.kindTint || '#002677';
              // Pick an icon based on the 'kind' label — fallback FileText
              const Icon = /webinar|video/i.test(f.kind) ? VideoCamera
                         : /story|blog|news/i.test(f.kind)     ? Newspaper
                         : FileText;
              return (
                <div
                  key={i}
                  className="bg-card border border-border rounded-sm p-6 hover:shadow-lg transition-shadow flex flex-col"
                  data-testid={`featured-${i}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className="text-[10px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 rounded-full"
                      style={{ background: `${tint}1A`, color: tint }}
                    >
                      {f.kind}
                    </span>
                  </div>
                  <Icon size={34} weight="duotone" className="text-[#002677] mb-3" />
                  <h3 className="font-heading text-lg font-semibold tracking-tight leading-snug">
                    {f.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1">
                    {f.desc}
                  </p>
                  <Link
                    to={
                      /whitepaper|pdf|research/i.test(f.kind) ? '/resources/whitepapers' :
                      /webinar|video/i.test(f.kind)            ? '/resources/webinars' :
                      /story|blog|news|article/i.test(f.kind)  ? '/resources/blog' :
                      /tutorial/i.test(f.kind)                 ? '/resources/tutorials' :
                      '/resources'
                    }
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#002677] hover:gap-2.5 transition-all"
                  >
                    {f.cta || 'Read more'} <Download size={14} weight="bold" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Two-column: Popular docs + Upcoming events */}
      <section className="py-16 lg:py-20 bg-background">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Popular docs */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-sm bg-[#002677]/10 flex items-center justify-center">
                <Code size={20} weight="duotone" className="text-[#002677]" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-semibold tracking-tight">Popular docs</h2>
                <p className="text-xs text-muted-foreground">Most-read pages by engineers & admins.</p>
              </div>
            </div>
            <ul className="bg-card border border-border rounded-sm divide-y divide-border" data-testid="popular-docs">
              {filteredDocs.length === 0 ? (
                <li className="p-5 text-sm text-muted-foreground">No docs match &ldquo;{q}&rdquo;.</li>
              ) : filteredDocs.map((d) => (
                <li key={d.label}>
                  <Link
                    to="/resources/docs"
                    className="flex items-center justify-between p-4 hover:bg-accent/30 transition-colors group"
                    data-testid={`doc-${d.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0,40)}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <BookOpen size={16} weight="duotone" className="text-[#002677] flex-shrink-0" />
                      <span className="text-sm font-medium truncate">{d.label}</span>
                      <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded hidden sm:inline-block">
                        {d.tag}
                      </span>
                    </div>
                    <ArrowRight size={14} weight="bold" className="text-muted-foreground group-hover:text-foreground group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Upcoming events */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-sm bg-[#E4002B]/10 flex items-center justify-center">
                <Calendar size={20} weight="duotone" className="text-[#E4002B]" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-semibold tracking-tight">Upcoming events</h2>
                <p className="text-xs text-muted-foreground">Virtual and in-person, worldwide.</p>
              </div>
            </div>
            <ul className="space-y-3" data-testid="upcoming-events">
              {events.map((e, i) => {
                const parts = (e.date || '').split(' ');
                const mon = parts[0] || '';
                const day = parts[1] || '';
                return (
                <li
                  key={i}
                  className="bg-card border border-border rounded-sm p-4 flex items-center gap-4 hover:border-foreground/30 transition-colors"
                  data-testid={`event-${i}`}
                >
                  <div className="flex-shrink-0 h-14 w-14 rounded-sm bg-[#002677] text-white flex flex-col items-center justify-center">
                    <span className="text-[10px] tracking-[0.15em] uppercase font-bold">{mon}</span>
                    <span className="font-heading text-lg font-bold leading-none">{day}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{e.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{e.location}</p>
                  </div>
                  <Link
                    to="/resources/webinars"
                    className="text-sm font-semibold text-[#002677] hover:underline flex-shrink-0"
                  >
                    {e.cta || 'Register'}
                  </Link>
                </li>
              );})}
            </ul>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-12 bg-[#f5f6fa] dark:bg-muted/20 border-t border-border">
        <div className="max-w-[900px] mx-auto px-6 bg-card border border-border rounded-sm p-8 text-center" data-testid="newsletter">
          <Sparkle size={28} weight="fill" className="text-[#002677] mx-auto mb-3" />
          <h3 className="font-heading text-2xl font-semibold tracking-tight">
            Get the BIL Digest.
          </h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-lg mx-auto">
            Once a month. Product drops, customer stories, and AI research — zero fluff.
          </p>
          <form
            onSubmit={(e) => { e.preventDefault(); }}
            className="mt-5 flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
          >
            <Input
              type="email"
              required
              placeholder="you@company.com"
              className="rounded-sm flex-1 h-11"
              data-testid="newsletter-email"
            />
            <button
              type="submit"
              className="h-11 px-5 rounded-sm font-medium text-white bg-[#002677] hover:bg-[#002677]/90 inline-flex items-center gap-2 transition-colors"
              data-testid="newsletter-submit"
            >
              Subscribe <ArrowRight size={14} weight="bold" />
            </button>
          </form>
          <p className="text-[11px] text-muted-foreground mt-3">
            Unsubscribe any time. See our <Link to="/register" className="underline">privacy policy</Link>.
          </p>
        </div>
      </section>
    </PublicPageShell>
  );
}
